import { AxiosInstance, AxiosResponse } from "axios";
import { PublicKey, VersionedMessage, VersionedTransaction } from "@solana/web3.js";

import { TransactionsWithSetup, VersionedTransactionResponse } from "../../types";
import { base64 } from "../bytes";

export function deserializeVersionedTransaction(response: VersionedTransactionResponse): VersionedTransaction {
    const message = VersionedMessage.deserialize(new Uint8Array(base64.decode(response.message)));
    const txn = new VersionedTransaction(message);
    response.signatures.forEach((sig) => {
        const deserializedSignature = new Uint8Array(base64.decode(sig.signature));
        txn.addSignature(new PublicKey(sig.publicKey), deserializedSignature);
    });
    return txn;
}

export function deserializeBase64VersionedTransaction(txBase64: string): VersionedTransaction {
    return VersionedTransaction.deserialize(new Uint8Array(base64.decode(txBase64)));
}

export async function fetchVersionedTransaction<BodyType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType
): Promise<VersionedTransactionResponse> {
    const response = await api.post<
        BodyType,
        AxiosResponse<VersionedTransactionResponse | { transaction: VersionedTransactionResponse }>
    >(url, data);
    if ("transaction" in response.data) {
        return response.data.transaction;
    }
    return response.data;
}

export async function fetchVersionedTransactions<BodyType, QueryType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType,
    queryParams?: QueryType
): Promise<VersionedTransactionResponse[]> {
    const response = await api.post<BodyType, AxiosResponse<VersionedTransactionResponse[]>>(url, data, {
        params: queryParams
    });
    return response.data;
}

export async function fetchAndDeserializeVersionedTransaction<BodyType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType
): Promise<VersionedTransaction> {
    const serializedTxn = await fetchVersionedTransaction(api, url, data);
    return deserializeVersionedTransaction(serializedTxn);
}

export async function fetchAndDeserializeVersionedTransactions<BodyType, QueryType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType,
    query?: QueryType
): Promise<VersionedTransaction[]> {
    const serializedTxns = await fetchVersionedTransactions(api, url, data, query);
    return serializedTxns.map((txn) => deserializeVersionedTransaction(txn));
}

export async function fetchAndDeserializeVersionedTransactionsWithSetup<BodyType, QueryType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType,
    query?: QueryType
): Promise<TransactionsWithSetup> {
    const serializedTxns = await fetchVersionedTransactions(api, url, data, query);
    return {
        setupTransactions: [deserializeVersionedTransaction(serializedTxns[0])],
        transactions: serializedTxns.slice(1).map((txn) => deserializeVersionedTransaction(txn))
    };
}

export async function fetchVersionedTransactionsWithLoanInfo<BodyType, LoanInfoType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType
): Promise<{ transactions: VersionedTransactionResponse[]; expectedLoanInfo: LoanInfoType }> {
    const response = await api.post<
        BodyType,
        AxiosResponse<{ transactions: VersionedTransactionResponse[]; expectedLoanInfo: LoanInfoType }>
    >(url, data);
    return response.data;
}

export async function fetchAndDeserializeVersionedTransactionsWithLoanInfo<BodyType, LoanInfoType>(
    api: AxiosInstance,
    url: string,
    data?: BodyType
): Promise<{ transactions: VersionedTransaction[]; expectedLoanInfo: LoanInfoType }> {
    const response = await fetchVersionedTransactionsWithLoanInfo<BodyType, LoanInfoType>(api, url, data);
    return {
        transactions: response.transactions.map((txn) => deserializeVersionedTransaction(txn)),
        expectedLoanInfo: response.expectedLoanInfo
    };
}
